import { Component, EventEmitter, Input, Output } from "@angular/core";
import { DndDropEvent } from 'ngx-drag-drop';

interface FieldGroup {
  parent: string;
  fields: any[];
  availableFields: any[];
  expanded: boolean;
}

@Component({
    selector: 'or-listbox',
    templateUrl: './list-box.component.html',
    styleUrls: ['./list-box.component.scss'],
    standalone: false
})
export class ListBoxComponent {
  @Input() public set showCount(show: boolean) { this._showCount = show; }
  @Input() public set headers(data) { this._headers = data; }
  @Input() public set items(data) {
    this._data = data;
    this.handleLoading();
  }

  @Input() public set loadSelected(data) {
    this._selectedFields = data ?? [];
    this.handleLoading();
  }

  @Output() selected = new EventEmitter();

  _showCount: boolean = false;
  _data = [];
  _headers = [];
  _selectedFields = [];
  _searchText = '';
  _groupedData: FieldGroup[] = [];
  _filteredGroups: FieldGroup[] = [];

  constructor() { }

  ngOnInit() {
    this.handleLoading();
  }

  handleLoading() {
    if (this._data?.length > 0) {
      this.buildGroups();
      this.updateAvailableFields();
    }
  }

  buildGroups() {
    const parents = [...new Set(this._data.map((item: any) => item?.parent).filter(Boolean))] as string[];
    // Preserve expanded state for existing groups
    const existingState = new Map(this._groupedData.map(g => [g.parent, g.expanded]));
    this._groupedData = parents.map(parent => ({
      parent,
      fields: this._data.filter((item: any) => item.parent === parent),
      availableFields: [],
      expanded: existingState.get(parent) ?? false
    }));
  }

  updateAvailableFields() {
    const search = this._searchText.toLowerCase().trim();
    for (const group of this._groupedData) {
      group.availableFields = group.fields.filter((f: any) =>
        !this._selectedFields.find((s: any) => s.id === f.id) &&
        (search === '' || f.label.toLowerCase().includes(search))
      );
    }
    if (search) {
      this._filteredGroups = this._groupedData.filter(g => g.availableFields.length > 0);
    } else {
      this._filteredGroups = this._groupedData;
    }
  }

  onSearch() {
    this.updateAvailableFields();
  }

  toggleGroup(group: FieldGroup) {
    group.expanded = !group.expanded;
  }

  addGroup(event: Event, group: FieldGroup) {
    event.stopPropagation();
    for (const field of [...group.availableFields]) {
      this.addRemoveField(field.id, 'add');
    }
  }

  addRemoveField(fieldId: string, action: string) {
    switch (action) {
      case 'add':
        const fieldToAdd = this._data.find((x: any) => x.id === fieldId);
        if (fieldToAdd && !this._selectedFields.find((x: any) => x.id === fieldId)) {
          this._selectedFields.push(fieldToAdd);
        }
        break;
      case 'remove':
        this._selectedFields = this._selectedFields.filter((x: any) => x.id !== fieldId);
        break;
      default:
        break;
    }
    this.updateAvailableFields();
    this.selected.emit(this._selectedFields);
  }

  onDrop(event: DndDropEvent) {
    if (event.dropEffect === 'move' && event.data) {
      const oldIndex = this._selectedFields.findIndex((f: any) => f.id === event.data.id);
      if (oldIndex !== -1) {
        const [item] = this._selectedFields.splice(oldIndex, 1);
        let index = event.index;
        if (typeof index === 'undefined') {
          index = this._selectedFields.length;
        }
        this._selectedFields.splice(index, 0, item);
        this.selected.emit(this._selectedFields);
      }
    }
  }

  getTypeIcon(type: string): string {
    switch (type) {
      case 'number': return 'fas fa-hashtag';
      case 'date': return 'far fa-calendar';
      case 'text': return 'fas fa-font';
      default: return 'fas fa-circle fa-xs';
    }
  }

  get totalAvailableCount(): number {
    return this._groupedData.reduce((sum, g) => sum + g.availableFields.length, 0);
  }
}
